import { DetailsPopup } from './DetailsPopup';

/**
 * The facts the desktop status line shows, for phones, in the shared popup shell. Reached from the
 * masthead's ⓘ.
 *
 * Shared by both surfaces on this frame: the rows are the same facts in the same order, and the
 * only thing that varies is what the identifier is CALLED — a tournament hand carries an id, a
 * curated problem a serial — which `idLabel` supplies rather than a second copy of the component.
 */
export function SessionDetailsPopup({
  onClose,
  streak,
  score,
  breakdown,
  handId,
  idLabel = 'ID',
  scoringType,
  difficulty,
  tournamentTitle,
  screens,
}: {
  onClose: () => void;
  /**
   * The current run. Optional, and its row is omitted when absent (feature 028) — the same rule
   * `StatusLine` and `MastheadInfoRow` follow: a session that keeps no run must not report "0",
   * because that states something untrue rather than something unknown. Counting practice keeps
   * no streak; both bidding surfaces do and pass it.
   */
  streak?: number;
  score: number;
  /**
   * The visit broken into its parts (feature 028). Optional: a session that shows only a running
   * total omits it and the three rows are not drawn, so /problems and module practice are
   * unchanged. Counting passes it, because its net score alone cannot distinguish a fresh start
   * from five right and five wrong — the pre-028 page showed the breakdown in exactly this popup.
   */
  breakdown?: { correct: number; incorrect: number; skipped: number };
  /**
   * The hand's identifier, or null for learners (who are not shown it). Strings as well as numbers
   * — see MastheadInfoRow's `handId`, and StatusLine's `idValue`.
   */
  handId: number | string | null;
  /** What that identifier is called. Matches StatusLine's `idLabel`. */
  idLabel?: 'ID' | 'Serial';
  scoringType: string;
  difficulty: number | null;
  tournamentTitle: string | null;
  /**
   * Which viewports the popup is available on, passed through to `DetailsPopup` (feature 028).
   *
   * It defaults to phone-only, because on the bidding surfaces the status line carries the same
   * facts from md up and a popup would be a second copy of what is already on screen. Counting
   * passes a value that shows at every width: its status line drops the tournament title on the
   * short bands to buy the height a play table needs, so this popup is that fact's only home
   * there — and an ⓘ button that opened nothing on desktop was the bug that made this necessary.
   */
  screens?: string;
}) {
  return (
    // The heading and the accessible name differ in case only because both are long-standing
    // strings the e2e suite matches on — kept exactly as they were.
    <DetailsPopup title="Problem Details" ariaLabel="Problem details" onClose={onClose} screens={screens}>
      {/* The two the masthead now shows only as a flame and a trophy — this is where
          the words live, and the only place a phone screen reader can reach them (the
          full StatusLine is display:none below md, so it is out of the a11y tree). */}
      {typeof streak === 'number' && (
        <div>
          <span className="text-ink-soft">Streak:</span>{' '}
          <span className="font-medium">{streak}</span>
        </div>
      )}
      <div>
        <span className="text-ink-soft">Score:</span> <span className="font-medium">{score}</span>
      </div>
      {breakdown && (
        <div data-testid="details-breakdown">
          <span className="text-ink-soft">This visit:</span>{' '}
          <span className="font-medium">
            {breakdown.correct} correct · {breakdown.incorrect} wrong · {breakdown.skipped} skipped
          </span>
        </div>
      )}
      {handId !== null && (
        <div>
          <span className="text-ink-soft">{idLabel}:</span>{' '}
          <span className="font-medium">{handId}</span>
        </div>
      )}
      <div>
        <span className="text-ink-soft">Scoring:</span>{' '}
        <span className="font-medium">{scoringType || 'N/A'}</span>
      </div>
      <div>
        <span className="text-ink-soft">Difficulty:</span>{' '}
        <span className="font-medium">
          {typeof difficulty === 'number' ? `${difficulty}/10` : 'N/A'}
        </span>
      </div>
      {tournamentTitle && (
        <div>
          <span className="text-ink-soft">From:</span>{' '}
          <span className="font-medium">{tournamentTitle}</span>
        </div>
      )}
    </DetailsPopup>
  );
}
